import type { ColumnSizingState, OnChangeFn } from '@tanstack/react-table';
import { useCallback, useEffect, useRef, useState } from 'react';

import { storage, StorageKeys } from '@/services/storage';

import { COLUMN_WIDTHS_STORAGE_KEY } from './constants';
import { clampColumnWidth } from './spotHistoryColumn';

function normalizeColumnWidths(widths: ColumnSizingState): ColumnSizingState {
  return Object.fromEntries(
    Object.entries(widths).map(([columnId, width]) => [columnId, clampColumnWidth(width)])
  );
}

function readColumnWidths(): ColumnSizingState {
  return normalizeColumnWidths(
    storage.get(StorageKeys.SpotHistoryColumnWidths)?.[COLUMN_WIDTHS_STORAGE_KEY] ?? {}
  );
}

export function useColumnWidths() {
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(readColumnWidths);
  const columnSizingRef = useRef(columnSizing);

  useEffect(() => {
    return storage.subscribe(StorageKeys.SpotHistoryColumnWidths, storedWidths => {
      const next = normalizeColumnWidths(storedWidths?.[COLUMN_WIDTHS_STORAGE_KEY] ?? {});
      columnSizingRef.current = next;
      setColumnSizing(next);
    });
  }, []);

  const onColumnSizingChange = useCallback<OnChangeFn<ColumnSizingState>>(updater => {
    setColumnSizing(current => {
      const next = normalizeColumnWidths(
        typeof updater === 'function' ? updater(current) : updater
      );
      columnSizingRef.current = next;
      return next;
    });
  }, []);

  const persistColumnWidth = useCallback((columnId: string, size: number) => {
    const next = {
      ...readColumnWidths(),
      ...columnSizingRef.current,
      [columnId]: clampColumnWidth(size)
    };
    columnSizingRef.current = next;
    setColumnSizing(next);
    const storedWidths = storage.get(StorageKeys.SpotHistoryColumnWidths) ?? {};
    storage.set(StorageKeys.SpotHistoryColumnWidths, {
      ...storedWidths,
      [COLUMN_WIDTHS_STORAGE_KEY]: next
    });
  }, []);

  return { columnSizing, onColumnSizingChange, persistColumnWidth };
}
